import { useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useI18n } from '@/i18n'
import { useToast } from '@/ui'
import {
  bindServiceWorker,
  mayApplySilently,
  onSafeToUpdateChange,
  setAppUpdateWaiting,
} from './appUpdate'

const UPDATE_CHECK_MS = 10 * 60_000

/**
 * Service-worker lifecycle. A new version goes in by itself, the way farmer-onboarding does it,
 * but never mid-work: only on a screen where a reload loses nothing (Home, Sync, More) with nobody
 * typing, or while the app is in the background. Elsewhere it waits (the toast offers it) until
 * the person is back on such a screen. Unsent work is in IndexedDB and survives the reload.
 */
export function UpdatePrompt() {
  const { t } = useI18n()
  const toast = useToast()
  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return
      bindServiceWorker(registration, (reload) => updateServiceWorker(reload))
      const check = () => void registration.update().catch(() => undefined)
      setInterval(check, UPDATE_CHECK_MS)
      // Also look when the app comes back to the front: that is when a phone is picked up again.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
    },
    onRegisterError(error) {
      console.error('Service worker registration failed', error)
    },
  })

  useEffect(() => setAppUpdateWaiting(needRefresh), [needRefresh])

  // Put the waiting version in as soon as it is safe.
  useEffect(() => {
    if (!needRefresh) return
    let done = false
    const tryApply = () => {
      if (done || !mayApplySilently()) return
      done = true
      void updateServiceWorker(true)
    }
    tryApply()
    const offSafe = onSafeToUpdateChange(tryApply)
    document.addEventListener('visibilitychange', tryApply)
    document.addEventListener('focusout', tryApply)
    return () => {
      offSafe()
      document.removeEventListener('visibilitychange', tryApply)
      document.removeEventListener('focusout', tryApply)
    }
  }, [needRefresh, updateServiceWorker])

  useEffect(() => {
    if (!needRefresh) return
    const id = toast.show({
      tone: 'info',
      title: t('pwa.updateTitle'),
      description: t('pwa.updateBody'),
      duration: 0,
      action: { label: t('pwa.updateAction'), onClick: () => void updateServiceWorker(true) },
    })
    return () => toast.dismiss(id)
  }, [needRefresh, t, toast, updateServiceWorker])

  useEffect(() => {
    if (!offlineReady) return
    toast.show({ tone: 'success', title: t('pwa.offlineReady') })
    setOfflineReady(false)
  }, [offlineReady, setOfflineReady, t, toast])

  return null
}
